import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { User as UserIcon, ArrowLeft, Save, Calendar, AlertCircle, Power, Trash2, Shield, Mail } from 'lucide-react';
import { User, UserRole, UserStatus } from '../../types/admin';
import { GET_USER, UPDATE_USER, ASSIGN_ROLE, DEACTIVATE_USER, DELETE_USER } from '../../graphql/admin';
import { useAuth } from '../../contexts/AuthContext';
import UserStatusBadge from '../../components/admin/UserStatusBadge';
import RoleAssignmentSelect from '../../components/admin/RoleAssignmentSelect';
import { ConfirmModal } from '../../components/Modal';

/**
 * UserDetailPage - Display and edit user information with role assignment
 * Requirements: 11.3, 11.4, 12.3
 */
const UserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { loading: authLoading, error: authError } = useAuth();
  
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Edit form state
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState<UserRole>(UserRole.USER);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Handle authorization errors - redirect to unauthorized page
  useEffect(() => {
    if (!authLoading && authError) {
      // Check if it's an authorization error
      if (authError.toLowerCase().includes('unauthorized') || 
          authError.toLowerCase().includes('forbidden') ||
          authError.toLowerCase().includes('not found')) {
        navigate('/unauthorized', { replace: true });
      }
    }
  }, [authLoading, authError, navigate]);

  const fetchUser = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);

      const isPreviewMode = import.meta.env.VITE_PREVIEW === 'true' || 
                           !import.meta.env.VITE_COGNITO_USER_POOL_ID;

      if (isPreviewMode) {
        // Mock data for preview mode
        const mockUser: User = {
          userId,
          tenantId: 'tenant-1',
          role: UserRole.USER,
          cognitoSub: 'mock-cognito-sub',
          email: 'user@example.com',
          name: 'John Doe',
          status: UserStatus.ACTIVE,
          createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          updatedAt: new Date().toISOString()
        };
        setUser(mockUser);
        setEditName(mockUser.name);
        setEditEmail(mockUser.email);
        setEditRole(mockUser.role);
        return;
      }

      const client = generateClient();
      const response = await client.graphql({
        query: GET_USER,
        variables: { userId }
      }) as any;

      if (response.data?.getUser) {
        const userData = response.data.getUser;
        setUser(userData);
        setEditName(userData.name);
        setEditEmail(userData.email);
        setEditRole(userData.role);
      } else {
        setError('User not found');
      }
    } catch (err: any) {
      console.error('Failed to fetch user:', err);
      setError(err.message || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleSave = async () => {
    if (!user || !editName.trim() || !editEmail.trim()) return;

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const isPreviewMode = import.meta.env.VITE_PREVIEW === 'true' || 
                           !import.meta.env.VITE_COGNITO_USER_POOL_ID;

      if (isPreviewMode) {
        setUser({ ...user, name: editName.trim(), email: editEmail.trim(), updatedAt: new Date().toISOString() });
        setIsEditing(false);
        setSuccessMessage('User updated successfully');
        return;
      }

      const client = generateClient();
      const response = await client.graphql({
        query: UPDATE_USER,
        variables: { 
          userId: user.userId, 
          input: { name: editName.trim(), email: editEmail.trim() } 
        }
      }) as any;

      if (response.data?.updateUser) {
        setUser(response.data.updateUser);
        setIsEditing(false);
        setSuccessMessage('User updated successfully');
      }
    } catch (err: any) {
      console.error('Failed to update user:', err);
      setError(err.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (newRole: UserRole) => {
    if (!user || newRole === user.role) return;

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const isPreviewMode = import.meta.env.VITE_PREVIEW === 'true' || 
                           !import.meta.env.VITE_COGNITO_USER_POOL_ID;

      if (isPreviewMode) {
        setUser({ ...user, role: newRole, updatedAt: new Date().toISOString() });
        setEditRole(newRole);
        setSuccessMessage('Role updated successfully');
        return;
      }

      const client = generateClient();
      const response = await client.graphql({
        query: ASSIGN_ROLE,
        variables: { input: { userId: user.userId, role: newRole } }
      }) as any;

      if (response.data?.assignRole) {
        setUser(response.data.assignRole);
        setEditRole(response.data.assignRole.role);
        setSuccessMessage('Role updated successfully');
      }
    } catch (err: any) {
      console.error('Failed to assign role:', err);
      setError(err.message || 'Failed to assign role');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!user) return;

    try {
      setSaving(true);
      setError(null);

      const isPreviewMode = import.meta.env.VITE_PREVIEW === 'true' || 
                           !import.meta.env.VITE_COGNITO_USER_POOL_ID;

      if (isPreviewMode) {
        setUser({ ...user, status: UserStatus.INACTIVE, updatedAt: new Date().toISOString() });
        setShowDeactivateModal(false);
        setSuccessMessage('User deactivated successfully');
        return;
      }

      const client = generateClient();
      const response = await client.graphql({
        query: DEACTIVATE_USER,
        variables: { userId: user.userId }
      }) as any;

      if (response.data?.deactivateUser) {
        setUser(response.data.deactivateUser);
        setSuccessMessage('User deactivated successfully');
      }
    } catch (err: any) {
      console.error('Failed to deactivate user:', err);
      setError(err.message || 'Failed to deactivate user');
    } finally {
      setSaving(false);
      setShowDeactivateModal(false);
    }
  };

  const handleDelete = async () => {
    if (!user) return;

    try {
      setSaving(true);
      setError(null);

      const isPreviewMode = import.meta.env.VITE_PREVIEW === 'true' || 
                           !import.meta.env.VITE_COGNITO_USER_POOL_ID;

      if (isPreviewMode) {
        setShowDeleteModal(false);
        window.location.href = '/admin/users';
        return;
      }

      const client = generateClient();
      await client.graphql({
        query: DELETE_USER,
        variables: { userId: user.userId }
      });

      window.location.href = '/admin/users';
    } catch (err: any) {
      console.error('Failed to delete user:', err);
      setError(err.message || 'Failed to delete user');
      setSaving(false);
      setShowDeleteModal(false);
    }
  };

  const getRoleLabel = (role: UserRole) => {
    switch (role) {
      case UserRole.PLATFORM_ADMIN:
        return 'Platform Admin';
      case UserRole.ADMIN:
        return 'Admin';
      case UserRole.USER:
        return 'User';
      default:
        return role;
    }
  };

  const getRoleBadgeStyles = (role: UserRole) => {
    switch (role) {
      case UserRole.PLATFORM_ADMIN:
        return 'bg-purple-100 text-purple-800';
      case UserRole.ADMIN:
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="space-y-6">
        <Link
          to="/admin/users"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Users
        </Link>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to="/admin/users"
        className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Users
      </Link>

      {/* Success message */}
      {successMessage && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-emerald-700">
          {successMessage}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 flex items-center">
          <AlertCircle className="w-4 h-4 mr-2" />
          {error}
        </div>
      )}

      {/* User header */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
              <UserIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{user.name}</h1>
              <p className="text-sm text-gray-500 flex items-center">
                <Mail className="w-4 h-4 mr-1" />
                {user.email}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeStyles(user.role)}`}>
              <Shield className="w-3 h-3 mr-1" />
              {getRoleLabel(user.role)}
            </span>
            <UserStatusBadge status={user.status} />
          </div>
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center text-gray-500 text-sm mb-1">
              <Shield className="w-4 h-4 mr-2" />
              Current Role
            </div>
            <p className="text-lg font-medium text-gray-900">{getRoleLabel(user.role)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center text-gray-500 text-sm mb-1">
              <Calendar className="w-4 h-4 mr-2" />
              Created
            </div>
            <p className="text-lg font-medium text-gray-900">
              {new Date(user.createdAt).toLocaleDateString()}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center text-gray-500 text-sm mb-1">
              <Calendar className="w-4 h-4 mr-2" />
              Last Updated
            </div>
            <p className="text-lg font-medium text-gray-900">
              {new Date(user.updatedAt).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* Edit form */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">User Details</h2>
        
        <div className="space-y-4">
          <div>
            <label htmlFor="userName" className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            {isEditing ? (
              <input
                id="userName"
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            ) : (
              <p className="text-gray-900">{user.name}</p>
            )}
          </div>

          <div>
            <label htmlFor="userEmail" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            {isEditing ? (
              <input
                id="userEmail"
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            ) : (
              <p className="text-gray-900">{user.email}</p>
            )}
          </div>

          <div className="flex items-center space-x-3 pt-4">
            {isEditing ? (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving || !editName.trim() || !editEmail.trim()}
                  className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditName(user.name);
                    setEditEmail(user.email);
                  }}
                  disabled={saving}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Edit
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Role assignment */}
      {user.role !== UserRole.PLATFORM_ADMIN && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Role Assignment</h2>
          <p className="text-sm text-gray-600 mb-4">
            Assign a role to this user. Admins can manage other users in the tenant.
          </p>
          <div className="max-w-xs">
            <RoleAssignmentSelect
              value={editRole}
              onChange={handleRoleChange}
              disabled={saving}
            />
          </div>
        </div>
      )}

      {/* Danger zone */}
      {user.status === UserStatus.ACTIVE && (
        <div className="bg-white rounded-xl shadow-lg border border-red-200 p-6">
          <h2 className="text-lg font-semibold text-red-600 mb-2">Danger Zone</h2>
          <p className="text-sm text-gray-600 mb-4">
            These actions are irreversible. Please proceed with caution.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowDeactivateModal(true)}
              className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 transition-colors"
            >
              <Power className="w-4 h-4 mr-2" />
              Deactivate User
            </button>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete User
            </button>
          </div>
        </div>
      )}

      {/* Deactivate confirmation modal */}
      {showDeactivateModal && (
        <ConfirmModal
          title="Deactivate User"
          message={`Are you sure you want to deactivate "${user.name}"? They will no longer be able to log in.`}
          confirmLabel="Deactivate"
          variant="danger"
          onConfirm={handleDeactivate}
          onCancel={() => setShowDeactivateModal(false)}
        />
      )}

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <ConfirmModal
          title="Delete User"
          message={`Are you sure you want to permanently delete "${user.name}"? This action cannot be undone.`}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default UserDetailPage;
